import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listUpcomingAppointments } from '@pc/db';
import { SmallCap } from '@pc/ui';
import { useActiveCtx } from '../useCtx';
import { APPOINTMENT_STATUS_LABELS } from '../types';

/**
 * Dashboard widget that surfaces the operator's next handful of
 * appointments. Rendered under BOTH the per-store shell and the org
 * shell -- the calendar module is org-aware (see
 * `packages/db/src/services/calendar.ts::listUpcomingAppointments`),
 * so when `ctx.storeId === null` (org mode) the underlying view
 * returns every appointment in the org and we add a "Store" badge per
 * row so the operator can tell which store each row belongs to.
 *
 * Registered via `calendarModule.dashboardWidgets` in
 * `packages/modules/calendar/src/module.tsx` so it appears on the
 * regular store dashboard when calendar is enabled. The org
 * dashboard at `apps/web/src/org/OrgDashboardPage.tsx` imports it
 * directly so it appears even when no other org-mode module
 * contributes widgets.
 */
export function UpcomingAppointmentsWidget() {
  const ctx = useActiveCtx();
  const { t, i18n } = useTranslation();
  const upcoming = useQuery({
    queryKey: ['calendar', 'upcoming', ctx.orgId, ctx.storeId, 8],
    queryFn: () => listUpcomingAppointments(ctx, 8),
    staleTime: 60_000,
  });

  if (upcoming.isLoading) {
    return <SmallCap>{t('actions.loading')}</SmallCap>;
  }
  if (upcoming.error) {
    return (
      <p className="text-xs text-clay">
        {t('calendar.widget.upcomingError')}
      </p>
    );
  }
  const rows = upcoming.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted">
        {t('calendar.widget.upcomingEmpty')}
      </p>
    );
  }

  const fmt = new Intl.DateTimeFormat(i18n.language || 'en', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <ul className="flex flex-col">
      {rows.map((r, i) => (
        <li
          key={r.id}
          className={
            i === 0
              ? 'py-2 text-sm'
              : 'py-2 text-sm border-t border-rule'
          }
        >
          <Link
            to={`/calendar/${r.id}`}
            className="block hover:bg-paper-2/30 -mx-1 px-1 rounded-sm"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-ink truncate">{r.title}</span>
              <span className="text-xs text-muted tabular shrink-0">
                {fmt.format(new Date(r.starts_at))}
              </span>
            </div>
            <div className="flex items-baseline gap-2 text-xs text-muted">
              {ctx.storeId === null ? (
                <span className="border border-rule px-1 py-px text-[10px] uppercase tracking-wider">
                  {r.store_name ?? t('calendar.widget.orgWide')}
                </span>
              ) : null}
              <span>
                {APPOINTMENT_STATUS_LABELS[r.status] ?? r.status}
              </span>
              {r.client_display_name ? (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{r.client_display_name}</span>
                </>
              ) : r.guest_name ? (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{r.guest_name}</span>
                </>
              ) : null}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
